import { Component, OnDestroy, inject } from '@angular/core';
import {VwuiModalService} from '@bvgo-ict/vwui-angular';
import {ProjectModalComponent} from '../../components/project-modal/project-modal.component';
import {BehaviorSubject, combineLatest, defer, merge, of, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, mapTo, skip, switchMap, take} from 'rxjs/operators';
import {ProjectReferenceService} from '../../services/project-reference.service';
import {AuthService} from '../../services/auth.service';
import {GraphService} from '../../services/graph.service';
import {ActivatedRoute, Router} from '@angular/router';
import {InfiniteScrollPaginator} from '../../utils/infinite-scroll-paginator';
import {UntypedFormControl} from '@angular/forms';
import {cleanFilter} from '../../utils/filter';
import {Authority} from '../../models/authority';

@Component({
    selector: 'app-project-reference-list',
    templateUrl: './project-reference-list.component.html',
    standalone: false
})
export class ProjectReferenceListComponent implements OnDestroy {
    private projectReferenceService = inject<ProjectReferenceService>('ProjectReferenceService' as any);
    private authService = inject<AuthService>('AuthService' as any);
    private graphService = inject<GraphService>('GraphService' as any);
    private vwuiModalService = inject(VwuiModalService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);

    private subscriptions: Subscription[] = [];

    showSuggestions = false;

    readonly isIframe = window !== window.parent

    listModeControl = new UntypedFormControl(this.isIframe ? 'table' : 'grid');
    searchControl = new UntypedFormControl(null);
    filterControl = new UntypedFormControl({});

    refresh$ = new BehaviorSubject<null>(null);

    suggestions$ = merge(
        this.searchControl.valueChanges,
        defer(() => of(this.searchControl.value))
    ).pipe(
        debounceTime(50),
        switchMap(query => query ? this.projectReferenceService.suggest(query) : of([])),
    );

    searchParameters$ = combineLatest([
        this.activatedRoute.queryParamMap,
        this.refresh$
    ]).pipe(
        map(([queryParams]) => ({
                filter: JSON.parse(queryParams.get('filter') || '{}'),
                query: queryParams.get('query') || ''
            })
        )
    )

    paginator$ = this.searchParameters$.pipe(
        switchMap(searchParameters => {
            const paginator = new InfiniteScrollPaginator(page => {
                return this.projectReferenceService.listReferences(searchParameters.query, searchParameters.filter, page);
            });
            return paginator.content$.pipe(
                mapTo(paginator)
            );
        })
    );

    facets$ = combineLatest([
        this.searchParameters$.pipe(
            distinctUntilChanged((a,b) => a.query === b.query),
        ),
        this.refresh$
    ]).pipe(
        debounceTime(50),
        switchMap(([searchParameters]) => this.projectReferenceService.getFacets(searchParameters.query)),
    );

    authorities = Authority

    constructor() {
        const queryParams = this.activatedRoute.snapshot.queryParamMap;
        if (queryParams.has('filter')) {
            this.filterControl.patchValue(JSON.parse(queryParams.get('filter') || '{}'));
        }
        if (queryParams.has('query')) {
            this.searchControl.patchValue(queryParams.get('query'));
        }

        this.subscriptions.push(this.filterControl.valueChanges.subscribe(() => {
            this.search();
        }));

        this.initListMode();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(it => it.unsubscribe())
    }

    openProjectModal(): void {
        this.vwuiModalService.open(ProjectModalComponent, {
            closeOnBackdropClick: false
        }).afterClosed.pipe(skip(1), take(1)).subscribe(_ => {
            this.refresh$.next(null);
        });
    }

    useSuggestion(suggestion: string): void {
        const tagRegex = /(<([^>]+)>)/ig;
        const plainTextSuggestion = suggestion.replace(tagRegex, '');
        this.search(plainTextSuggestion);
        this.showSuggestions = false;
        // This timeout is needed to work around some weird bug setting the value back to the previous value otherwise
        setTimeout(() => this.searchControl.patchValue(plainTextSuggestion));
    }

    onBlur(): void {
        this.showSuggestions = false;
    }

    onFocus(): void {
        this.showSuggestions = true;
    }

    search(query?: string): void {
        const queryParams: {[key: string]: any} = {filter: JSON.stringify(cleanFilter(this.filterControl.value))}
        if (query !== undefined) {
            queryParams.query = query;
        }

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge',
            queryParams
        });
    }

    private initListMode(): void {
        const queryListMode = this.activatedRoute.snapshot.queryParamMap.get('listMode');
        if(queryListMode) {
            this.listModeControl.patchValue(queryListMode);
        }

        this.subscriptions.push(
            this.listModeControl.valueChanges.subscribe(listMode => {
                this.updateListModeQueryRoute(listMode);
            })
        );
    }

    private updateListModeQueryRoute(listMode: string): void {
        this.router.navigate(
            [],
            {
                relativeTo: this.activatedRoute,
                queryParams: {
                    listMode
                },
                queryParamsHandling: 'merge'
            }
        );
    }
}
